import { GridFilterModel, GridFilterItem } from '@mui/x-data-grid-pro'
import { useState, useCallback, useRef, useMemo, useEffect } from 'react'

import { FeedbackGridApi } from '../api/feedbackGridApi'
import { FilterValue, FeedbackGridColumn, SystemColumn, FilterOption } from '../types/feedbackGrid.types'

interface UseGridFiltersProps {
	api: FeedbackGridApi
	columns: FeedbackGridColumn[]
	systemColumns: SystemColumn[]
	initialFilters: FilterValue
}

export const useGridFilters = ({ api, columns, systemColumns, initialFilters }: UseGridFiltersProps) => {
	// Normalize initial filters and extract initial options
	const { normalizedInitialFilters, initialFilterOptions } = useMemo(() => {
		const normalized: FilterValue = {}
		const options: Record<string, FilterOption[]> = {}

		Object.entries(initialFilters).forEach(([key, value]) => {
			if (value && typeof value === 'object' && 'values' in value && 'options' in value) {
				normalized[key] = value.values
				options[key] = value.options
			} else {
				normalized[key] = value
			}
		})

		return { normalizedInitialFilters: normalized, initialFilterOptions: options }
	}, [initialFilters])

	// Mappings between grid fields and backend filter keys
	const fieldToFilterKey = useMemo(() => {
		const mapping: Record<string, string> = {}
		columns.forEach((col) => {
			mapping[col.normalizedColumn] = col.normalizedColumn
		})
		systemColumns.forEach((col) => {
			const field = typeof col.column === 'string' ? col.column.replace('feedbacks.', '') : col.translation
			if (col.filter_identificator) {
				mapping[field] = col.filter_identificator
			}
		})
		return mapping
	}, [columns, systemColumns])

	const filterKeyToField = useMemo(() => {
		const mapping: Record<string, string> = {}
		Object.entries(fieldToFilterKey).forEach(([field, filterKey]) => {
			mapping[filterKey] = field
		})
		return mapping
	}, [fieldToFilterKey])

	// Convert normalized initial filters to GridFilterModel
	const initialFilterModel: GridFilterModel = useMemo(() => {
		const items: GridFilterItem[] = []
		Object.entries(normalizedInitialFilters).forEach(([key, value]) => {
			const field = filterKeyToField[key] || key
			if (value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)) {
				items.push({
					id: field,
					field,
					operator: Array.isArray(value) ? 'isAnyOf' : 'contains',
					value,
				})
			}
		})
		return { items }
	}, [normalizedInitialFilters, filterKeyToField])

	const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel)

	// Filter options state
	const [filterOptions, setFilterOptions] = useState<Record<string, FilterOption[]>>(() => initialFilterOptions)
	const [loadingOptions, setLoadingOptions] = useState<Record<string, boolean>>({})

	// Update filter options if initialFilterOptions changes
	useEffect(() => {
		if (Object.keys(initialFilterOptions).length > 0) {
			setFilterOptions((prev) => ({ ...prev, ...initialFilterOptions }))
		}
	}, [initialFilterOptions])

	const loadFilterOptions = useCallback(
		async (field: string) => {
			const filterKey = fieldToFilterKey[field] || field
			if (filterOptions[filterKey] || loadingOptions[filterKey]) {
				return
			}

			setLoadingOptions((prev) => ({ ...prev, [filterKey]: true }))
			try {
				const options = await api.fetchFilterOptions(filterKey)
				setFilterOptions((prev) => ({ ...prev, [filterKey]: options }))
			} catch (err) {
				console.error(`Failed to load filter options for ${filterKey}:`, err)
			} finally {
				setLoadingOptions((prev) => ({ ...prev, [filterKey]: false }))
			}
		},
		[api, fieldToFilterKey, filterOptions, loadingOptions],
	)

	// Ref for internal MUI filter model to allow immediate UI updates without waiting for debounce
	const internalFilterModelRef = useRef<GridFilterModel>(initialFilterModel)

	const handleFilterModelChange = useCallback((newModel: GridFilterModel) => {
		setFilterModel(newModel)
		internalFilterModelRef.current = newModel
	}, [])

	const getFiltersForBackend = useCallback(
		(model: GridFilterModel): FilterValue => {
			const result: FilterValue = {}
			model.items.forEach((item) => {
				const filterKey = fieldToFilterKey[item.field] || item.field
				if (item.value !== undefined && item.value !== null && item.value !== '') {
					if (Array.isArray(item.value) && item.value.length === 0) {
						result[filterKey] = []
						return
					}
					result[filterKey] = item.value
				} else {
					// Handle cases where item.value is empty string or null (for text filters or cleared select)
					result[filterKey] = Array.isArray(item.value) ? [] : null
				}
			})
			return result
		},
		[fieldToFilterKey],
	)

	const applyFiltersToBackend = useCallback(
		async (filterValues: FilterValue) => {
			try {
				await api.applyFilters(filterValues)
			} catch (err) {
				console.error('Failed to apply filters:', err)
			}
		},
		[api],
	)

	return {
		filterModel,
		handleFilterModelChange,
		internalFilterModelRef,
		getFiltersForBackend,
		applyFiltersToBackend,
		filterOptions,
		loadingOptions,
		loadFilterOptions,
		fieldToFilterKey,
		filterKeyToField,
		initialFilterModel,
	}
}
